<script setup>
  import {ref, reactive, computed, watch} from 'vue';

  import {titleCase, mulberry32} from "../utilities.js"
  import MOVE_DATA from '../data/move-incidence.json';

  import BaseCard from "../components/BaseCard.vue";
  import ToolPageHeader from "../components/ToolPageHeader.vue";
  import TypeSelect from "../components/TypeSelect.vue"
  import TypeBadge from "../components/TypeBadge.vue"
  import InputGroup from "../components/InputGroup.vue"
  import InfoHover from "../components/InfoHover.vue"
  import CopyFormButton from "../components/CopyFormButton.vue";

  const bodyTypes = {
    'armor': 'insectoid',
    'arms': 'head with arms',
    'ball': 'ball',
    'blob': 'head on base',
    'bug-wings': 'winged insect',
    'fish': 'fish',
    'heads': 'multiple bodies',
    'humanoid': 'humanoid',
    'legs': 'head with legs',
    'quadruped': 'quadruped',
    'squiggle': 'serpentine',
    'tentacles': 'tentacles',
    'upright': 'upright with tail',
    'wings': 'winged',
  };


  const formDefaults = {
    type1: '-',
    type2: '-',
    evoStage: '',
    bodyShape: '',
    seed: 0,
    includeMoves: '',
    textTemplate: "{lv}\t{name}",
    ratio: 3 / 2,
  }

  // for(const [key, value] of new URLSearchParams(window.location.hash)) {


  const form = reactive({
    ...formDefaults
  });

  const formMutator = (key, value)=>{
    form[key] = value;
  }

  const useRandom = ref(false);
  const top = ref(0);
  const levelShortlists = reactive({});
  const missingMoves = reactive([]);


  const moveResults = computed(() => {
    let getLevelRow = (lv, level_shortlist, random_number) => {
      var choice = seededBandedRandomChoice(level_shortlist.map((x) => x['chance']), random_number);

      // console.log(level_shortlist[0]['chance'], ">", top * ratio, (level_shortlist[0]['chance'] > top * ratio) )
      if (level_shortlist[choice]['chance'] > top.value * form.ratio) {
        return {
          'lv': lv,
          'id': level_shortlist[choice]['id'],
          'chance': level_shortlist[choice]['chance'],
          'move': level_shortlist[choice],
        };
      }
    };


    if (!Object.keys(levelShortlists).length) {
      return null;
    }

    // console.log(state_values)

    const randgen = mulberry32(parseInt(form.seed));
    let rows = [];
    for (var lv in levelShortlists) {

      if (levelShortlists[lv].length) {
        let row = getLevelRow(lv, levelShortlists[lv], randgen());
        if (row) {
          rows.push(row);
        }
      }
    }

    let dupe_tracker = {};
    for (var r in rows) {
      let row = rows[r];
      if (!(row['id'] in dupe_tracker)) {
        dupe_tracker[row['id']] = [];
      }
      dupe_tracker[row['id']].push(row);
    }


    let no_dupes = [];
    for (var m_id in dupe_tracker) {

      if (dupe_tracker[m_id].length === 1) {
        no_dupes.push(dupe_tracker[m_id][0]);
      } else {
        //grab the one with the highest chance

        var chance_sort = dupe_tracker[m_id].sort(function (a, b) {
          return b['chance'] - a['chance'];
        });

        no_dupes.push(chance_sort[0])
      }
    }


    let no_dupes_sorted = no_dupes.sort(function (a, b) {
      return parseInt(a['lv']) - parseInt(b['lv']);
    })

    // console.log(no_dupes, no_dupes_sorted);


    return no_dupes_sorted
  });

  const moveResultsText = computed(() => {
    if (!moveResults.value){
      return '';
    }
    return moveResults.value.map((row)=>{
      return form.textTemplate
        .replace('{lv}', row.lv)
        .replace('{name}', row.move.name)
        .replace('{type}', row.move.type);
    }).join("\n");
  });




  const seededBandedRandomChoice = (items, random_number) => {
      let sum = items.reduce((a, b) => a + b, 0);
      let normalised = 1. / sum;

      // var seeder = mulberry32(seed)
      let total = 0;
      //let choice = -1;
      for (var i in items) {
        total += items[i] * normalised;
        if (total > random_number) {
          return i;
        }
      }
    }

  let oldSeed = 1;
  watch(useRandom, () => {
    if (useRandom.value){
      form.seed = oldSeed;
    } else {
      oldSeed = form.seed;
      form.seed = 0;
    }
  })


  watch(form, () => {
    // console.log("generateMoveList!")

    let additives = [];
    if (form.type1 !== '') {
      additives.push('type-' + form.type1);
    }
    if (form.type2 !== '') {
      additives.push('type-' + form.type2);
    }
    if (form.evoStage !== '') {
      additives.push('evo_stage-' + form.evoStage);
    }
    if (form.bodyShape !== '') {
      additives.push('bodyshape-' + bodyTypes[form.bodyShape]);
    }


    //this goes up here for -bite, -tackle
    let includeMovesList = form.includeMoves.toLowerCase().trim().split(/\s*,\s*/).filter((v)=>v.length == 0);
    missingMoves.splice(0); // empties
    let found_moves = []; //diff against include moves to find typos etc




    for (var k in levelShortlists){
      delete levelShortlists[k];
    }
    for (let move_id in MOVE_DATA) {
      let move = MOVE_DATA[move_id]

      if (includeMovesList.includes('-'+move['name'].toLowerCase())) {
        found_moves.push(move['name'].toLowerCase());
        //remove from level chances lol
        continue;
      }


      let incidence = 0;//move['total'];

      for (let a in additives) {
        let additive = additives[a];
        if (move[additive] !== 0. && !isNaN(move[additive])) {
          incidence += move[additive];
        }
      }

      for (let lv in move['levels']) {
        if (!(lv in levelShortlists)) {
          levelShortlists[lv] = [];
        }

        let chance = incidence * move['levels'][lv];
        if (chance !== 0) {
          levelShortlists[lv].push({ 'id': move['id'], 'name': move['name'], 'type': move['type'], 'chance': chance });
        }
      }
    }


    let bests = [];

    for (let lv in levelShortlists) {
      if (levelShortlists[lv].length) {
        bests.push(levelShortlists[lv][0]['chance'])

        //dont need to sort whn we're not picking the top one automatically
        // levelShortlists[lv].sort(function(a, b) {
        //     return b['chance'] - a['chance'];
        // });
      }
    }

    // let levels = Object.keys(levelShortlists).sort(function(a, b) {
    //     return parseInt(a) - parseInt(b);
    // });


    top.value = Math.max(...bests);




    // if there are includeMovesList set, we need to pick the most common level for those moves (from this type/stage etc)

    if (includeMovesList.length) {

      var level_picks_per_move = {}; //in case of a level collision, store a few options per move so we can fallback

      for (let move_id in MOVE_DATA) {
        let move = MOVE_DATA[move_id];

        if (includeMovesList.includes(move['name'].toLowerCase())) {
          found_moves.push(move['name'].toLowerCase());

          let level_chances = []; // we'll store as level: chance and sort later

          for (let lv in move['levels']) {
            let chance = move['levels'][lv];

            for (let a in additives) {
              if (move[additives[a]] !== 0. && !isNaN(move[additives[a]])) {
                chance += move[additives[a]];
              }
            }
            if (chance !== 0.) {
              level_chances.push([lv, chance])
            }
          }


          level_picks_per_move[move_id] = level_chances.sort(function (a, b) {
            return b[1] - a[1];
          })

        }
      }

      if (found_moves.length !== includeMovesList.length) {
        missingMoves.splice(0); // empties
        // console.log("Missing moves:", missingMoves);
      }


      //how to sort these now............
      //chances etc
      //if there's a level collision, higher chance gets the level, loser gets its second highest chance level
      //er.... better while that?
      //so first, levels-orderd-by-chance for each move

      let level_arrangement = {};// final arrangement

      let move_level_fallbacks = {};// track how many tries for this move


      let tryPlaceAtLevel = (move_id, lv_choice = 0)=>{
        let lv = level_picks_per_move[move_id][lv_choice][0];
        move_level_fallbacks[move_id] = lv_choice; //are we placing at first/0th choice or further down

        if (level_arrangement[lv] === undefined) {
          level_arrangement[lv] = move_id
        } else {

          //we have a level collision - fight!
          let existing_move_id = level_arrangement[lv];
          if (level_picks_per_move[move_id][lv_choice][1] > level_picks_per_move[existing_move_id][move_level_fallbacks[existing_move_id]][1]) {
            //steal the spot, move existing elsewhere
            level_arrangement[lv] = move_id;
            tryPlaceAtLevel(existing_move_id, move_level_fallbacks[existing_move_id] + 1);
          } else {
            //try to place at my second best level
            tryPlaceAtLevel(move_id, move_level_fallbacks[move_id] + 1);
          }

        }
      }

      // console.log("level_picks_per_move:", level_picks_per_move);
      for (let move_id in level_picks_per_move) {
        tryPlaceAtLevel(move_id);
      }

      // console.log("level_arrangement:", level_arrangement);

      for (let lv in level_arrangement) {
        let move = MOVE_DATA[level_arrangement[lv]];
        if (levelShortlists[lv] === undefined) {
          levelShortlists[lv] = [];
        }
        levelShortlists[lv].push({
          'id': move['id'], 'name': move['name'], 'type': move['type'],
          'chance': level_picks_per_move[move['id']][move_level_fallbacks[move['id']]][1] + 100
        });
      }

      // console.log("levelShortlists:", levelShortlists);
    }
  })

</script>

<template>
  <div class="container mt-8 mb-20">
    <ToolPageHeader tool="movelist-generator">
      <template #info>
        <p class="my-2">Moves are picked by likelihood of occurrence in the in-game Pokémons' movelists, depending on things like typing and evolution stage.</p>
        <p class="my-2">At each level, the seed can be used to pick a move from the weighted-random pool for that level. Try different seeds!</p>
        <p class="my-2">Reduce the threshold to get more moves. Raise it to get fewer, but more likely moves. And remember to customise it to fit your Pokémon!</p>
      </template>
      <template #controls>
        <CopyFormButton tool="movelist-generator" :form="form" :form-defaults="formDefaults" :form-mutator="formMutator"/>
      </template>
    </ToolPageHeader>


    <BaseCard tool="movelist-generator" class="my-12 flex flex-col gap-8">

      <div class="flex flex-col lg:grid lg:grid-cols-12 gap-8">

        <InputGroup class="col-span-5" label-key="ts1" input-class="!outline-tool-movelist-generator">
          <template #label>Types:</template>
          <template #input>
            <TypeSelect v-model="form.type1" :show-none="true"/>
            <TypeSelect v-model="form.type2" :show-none="true"/>
          </template>
        </InputGroup>

        <InputGroup class="col-span-2" label-key="evostage" input-class="!outline-tool-movelist-generator">
          <template #label>Evo <span class="lg:max-xl:hidden">Stage</span>:</template>
          <template #input>
            <select class="min-w-[3em]" v-model="form.evoStage">
              <option value="">-</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
            </select>
          </template>
        </InputGroup>

        <InputGroup class="col-span-5" label-key="bodyshape" input-class="!outline-tool-movelist-generator">
          <template #label>Body Shape:</template>
          <template #input>
            <select class="min-w-[3em]" v-model="form.bodyShape">
              <option value="">-</option>
              <option
                v-for="key in Object.keys(bodyTypes)"
                :key="'bs-'+key"
                :value="key"
              >
                {{ titleCase(bodyTypes[key]) }}
              </option>
            </select>
            <div class="bg-gray-300 dark:bg-gray-600 min-w-[3em] text-center !grow-0">
              <img
                  v-if="form.bodyShape"
                  :src="$router.options.base+'/images/bodyshapes/' + form.bodyShape + '.png'"
                  :alt="form.bodyShape"
                  class="max-h-[2em] ml-2 mt-1"
                />
            </div>
          </template>
        </InputGroup>

      </div>


      <div class="flex flex-col lg:grid lg:grid-cols-12 gap-8">

        <InputGroup class="col-span-10" label-key="moves" input-class="!outline-tool-movelist-generator">
          <template #label>
            Moves to Include:
            <p class="text-xs mt-2">Comma-separated, mind the spelling.<br/>Use e.g. -Bite to exclude moves.</p>
          </template>
          <template #input>
            <textarea class="resize-none" v-model="form.includeMoves"/>
          </template>
        </InputGroup>


        <InputGroup class="col-span-2" label-key="seed" input-class="!outline-tool-movelist-generator" :vertical="true">
          <template #label>
            <label for="use-random">
              <InfoHover text="A seed of zero takes all randomness out, leaving only the most likely moves per level."/>
              <input id="use-random" type="checkbox" v-model="useRandom" class="float-left mt-1 mr-1"/>
              Random Seed
            </label>
          </template>
          <template #input>
            <div class="grid grid-cols-4">
              <input
                type="number"
                pattern="[0-9]*"
                min="0"
                :class="[
                  'col-span-2 text-lg',
                  useRandom ? '' : 'pointer-events-none greyed-out'
                ]"
                v-model="form.seed"
                :disabled="!useRandom"
              />
              <div
                :class="[
                  'cursor-pointer bg-tool-movelist-generator/90 hover:bg-tool-movelist-generator/75 text-white text-center p-4',
                  useRandom ? '' : 'pointer-events-none greyed-out'
                ]"
                @click="()=>{form.seed = Math.max(0, form.seed-1)}"
              >-</div>
              <div
                :class="[
                  'cursor-pointer bg-tool-movelist-generator hover:bg-tool-movelist-generator/75 text-white text-center p-4',
                  useRandom ? '' : 'pointer-events-none greyed-out'
                ]"
                @click="()=>{form.seed += 1}"
              >+</div>
            </div>
          </template>
        </InputGroup>

      </div>
    </BaseCard>


    <BaseCard tool="movelist-generator" class="my-12 flex flex-col gap-8">

      <InputGroup class="mb-8" label-key="ratio" input-class="!outline-tool-movelist-generator">
        <template #label>
          <InfoHover text="A lower value lets less common or likely moves into the list."/>
          Likelihood Threshold:
        </template>
        <template #input>
          <input class="mx-4" type="range" v-model="form.ratio" step="0.25" max="25"/>
          <input readonly class="bg-gray-300 dark:bg-gray-600 md:!w-[3em] text-center !grow-0" :value="form.ratio * 4" />
        </template>
      </InputGroup>


      <h2 class="text-2xl mb-4 text-tool-movelist-generator">Results:</h2>

      <div class="grid lg:grid-cols-2 gap-8 items-stretch">

        <table class="styled-table">
          <thead>
            <tr>
              <th>Level</th>
              <th>Move</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row, i in moveResults"
              :key="i"
            >
              <td>{{ row['lv'] }}</td>
              <td class="font-bold">{{ row['move']['name'] }}</td>
              <td><TypeBadge :type-id="row['move']['type']"/></td>
            </tr>
          </tbody>
        </table>

        <div class="flex flex-col gap-4">
          <InputGroup label-key="template" input-class="!outline-tool-movelist-generator">
            <template #label>
              <InfoHover text="Available keys: {lv} {name} {type}"/>
              Text line template:
            </template>
            <template #input>
              <input type="text" class="font-mono" v-model="form.textTemplate"/>
            </template>
          </InputGroup>

          <textarea class="grow resize-none shadow-lg border border-gray-300 rounded-lg font-mono" readonly v-model="moveResultsText"></textarea>
        </div>

      </div>

    </BaseCard>


  </div>
</template>
